import { useState } from "react";
import { Calculator } from "lucide-react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { NumInput } from "@/components/NumInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtIDR } from "@/lib/constants";

const CURRENCIES = { JPY: "¥", KRW: "₩", EUR: "€", HKD: "HK$", AUD: "A$", USD: "$" };

// Universal markup-on-cost options (applies to all batches/currencies)
const MARKUPS = [0.5, 0.7, 1.0];
// Round UP to nearest Rp5.000 (epsilon guards float noise so exact multiples stay unchanged)
const roundUp5k = (v) => Math.ceil((Math.round(v) - 1e-6) / 5000) * 5000;

export default function QuickCalculator({ batch, onUsePrice, triggerTestId = "calculator-button", compact = false }) {
  const [open, setOpen] = useState(false);
  const [foreign, setForeign] = useState("");
  const [manualCur, setManualCur] = useState("JPY");
  const [manualRate, setManualRate] = useState("");

  const rate = batch?.exchange_rate_to_idr ?? (parseFloat(manualRate) || null);
  const code = batch?.currency_code ?? manualCur;
  const symbol = batch?.currency_symbol ?? CURRENCIES[code] ?? code;

  const f = parseFloat(foreign) || 0;
  const cost = rate && f > 0 ? f * rate : null;
  const recs =
    cost != null
      ? MARKUPS.map((pct) => {
          const price = roundUp5k(cost * (1 + pct)); // markup first, round UP, then profit from rounded price
          return { pct, price, profit: price - cost };
        })
      : null;

  return (
    <>
      {compact ? (
        <button
          type="button"
          data-testid={triggerTestId}
          aria-label="Quick calculator"
          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
          onClick={() => setOpen(true)}
        >
          <Calculator className="h-4 w-4" />
        </button>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="icon"
          data-testid={triggerTestId}
          aria-label="Quick calculator"
          className="h-10 w-10 shrink-0"
          onClick={() => setOpen(true)}
        >
          <Calculator className="h-5 w-5" />
        </Button>
      )}
      <Drawer open={open} onOpenChange={setOpen} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Quick Calculator</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-4 px-4 pb-6">
            {batch ? (
              <div data-testid="calc-batch-context" className="rounded-md border bg-accent px-3 py-2 text-sm">
                <p className="font-bold">{batch.name}</p>
                <p className="text-muted-foreground">
                  {batch.region} · {code} · {symbol}1 = {fmtIDR(batch.exchange_rate_to_idr)}
                </p>
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Currency</Label>
                  <Select value={manualCur} onValueChange={setManualCur}>
                    <SelectTrigger data-testid="calc-manual-currency" className="h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(CURRENCIES).map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Rate (1 unit = IDR)</Label>
                  <NumInput data-testid="calc-manual-rate" mode="decimal" className="h-12" placeholder="115" value={manualRate} onValue={setManualRate} />
                </div>
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Buying price ({symbol})</Label>
              <NumInput
                data-testid="calc-price-input"
                mode="decimal"
                className="h-14 text-xl font-bold"
                placeholder="0"
                value={foreign}
                onValue={setForeign}
              />
            </div>

            {cost != null && (
              <div data-testid="calc-cost-row" className="flex items-center justify-between rounded-md border p-3">
                <span className="text-sm text-muted-foreground">
                  Cost in IDR ({symbol}
                  {f.toLocaleString("en-US")})
                </span>
                <span data-testid="calc-cost-idr" className="font-heading text-xl font-extrabold">
                  {fmtIDR(cost)}
                </span>
              </div>
            )}

            {recs && (
              <div data-testid="calc-recs" className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Recommended selling price</p>
                {recs.map((r) => (
                  <div key={r.pct} data-testid={`calc-rec-${Math.round(r.pct * 100)}`} className="flex items-center justify-between rounded-md border bg-white p-3">
                    <div>
                      <p className="text-sm font-bold">+{Math.round(r.pct * 100)}% MARKUP</p>
                      <p data-testid={`calc-rec-laba-${Math.round(r.pct * 100)}`} className="text-xs text-muted-foreground">
                        Laba {fmtIDR(r.profit)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Jual</p>
                      <p data-testid={`calc-rec-jual-${Math.round(r.pct * 100)}`} className="font-heading text-xl font-extrabold">
                        {fmtIDR(r.price)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {onUsePrice && (
              <Button type="button" data-testid="calc-use-price" className="h-12 w-full" disabled={f <= 0} onClick={() => { onUsePrice(f); setOpen(false); }}>
                Use as Buying Price
              </Button>
            )}
            <p className="text-center text-xs text-muted-foreground">Informational only — nothing is saved or changed.</p>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
